import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { expenseService } from "../../services/expenseService";
import toast from "react-hot-toast";
import { vendorService } from "../../services/vendorService";
import "../../styles/components/expenses/ExpenseList.css";
import ConfirmationModal from "../common/ConfirmationModal";
import { exportCSV, exportExcel, exportPDF } from "../../utils/exportExpenseHelpers";

const ExpenseList = ({ refreshFlag, onEditExpense, hideActions = false, todayOnly = false }) => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [dateFilter, setDateFilter] = useState({ from: "", to: "" });
  const [expenses, setExpenses] = useState([]);
  const [vendorFilter, setVendorFilter] = useState("");
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const fetchExpenses = async () => {
    try {
      const response = await expenseService.getAllExpenses();
      if (response.data.success) {
        setExpenses(response.data.expenses);
      }
    } catch (error) {
      toast.error("Failed to load expenses");
      console.error("Error fetching expenses:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadVendors = async () => {
      try {
        const res = await vendorService.getAllVendors();
        if (res.data.success) {
          setVendors(res.data.vendors);
        }
      } catch (error) {
        toast.error("Failed to load vendors");
        console.error("Vendor fetch error:", error);
      }
    };

    loadVendors();
  }, []);


  useEffect(() => {
    fetchExpenses();
  }, [refreshFlag]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, categoryFilter, dateFilter, statusFilter, vendorFilter, supplierFilter]);

  const filteredExpenses = expenses.filter((exp) => {
    const expDate = new Date(exp.expense_date);
    const fromDate = dateFilter.from ? new Date(dateFilter.from) : null;
    const toDate = dateFilter.to ? new Date(dateFilter.to) : null;

    // Filter by today if todayOnly is true
    if (todayOnly) {
      const today = new Date().toDateString();
      if (expDate.toDateString() !== today) return false;
    }

    const matchesCategory = !categoryFilter || exp.category === categoryFilter;
    const matchesVendor = !vendorFilter || exp.vendor_id == vendorFilter;
    const matchesStatus = !statusFilter || exp.status === statusFilter;
    const matchesSupplier = !supplierFilter || exp.supplier === supplierFilter;
    const matchesSearch =
      !searchQuery ||
      exp.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (exp.supplier || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFrom = !fromDate || expDate >= fromDate;
    const matchesTo = !toDate || expDate <= toDate;

    return matchesVendor && matchesCategory && matchesSearch && matchesFrom && matchesTo && matchesStatus && matchesSupplier;
  });

  // Extract unique suppliers from expenses
  const uniqueSuppliers = [...new Set(expenses.map(exp => exp.supplier).filter(Boolean))].sort();

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentExpenses = filteredExpenses.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage);

  const confirmDelete = (expense) => {
    setExpenseToDelete(expense);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    try {
      await expenseService.deleteExpense(expenseToDelete.id);
      toast.success("Expense deleted successfully");
      fetchExpenses();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete expense");
    } finally {
      setDeleteModalOpen(false);
      setExpenseToDelete(null);
    }
  };

  const handleDeleteCancelled = () => {
    setDeleteModalOpen(false);
    setExpenseToDelete(null);
  };

  const handleEdit = (expense) => {
    if (onEditExpense) onEditExpense(expense);
  };

  const handleSettle = async (id) => {
    try {
      await expenseService.settleExpense(id);
      toast.success("Expense settled successfully");
      fetchExpenses();
    } catch (error) {
      console.error("Settle error:", error);
      toast.error("Failed to settle expense");
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          className={`page-btn ${i === currentPage ? "active" : ""}`}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  if (loading) return <div className="expenses-loading">Loading expenses...</div>;
  if (!expenses.length) return <div className="no-expenses">No expenses recorded yet.</div>;

  return (
    <div className="expense-list-container">
      <div style={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
        <h2 className="list-title">All Expenses</h2>
        {hideActions && (
          <div className="filter_btn" style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <button onClick={() => exportCSV(currentExpenses, "expenses.csv", vendors)}>CSV</button>
            <button onClick={() => exportExcel(currentExpenses, "expenses.xlsx", vendors)}>Excel</button>
            <button onClick={() => exportPDF(currentExpenses, "expenses.pdf", vendors, user?.tenant_logo)}>PDF</button>
          </div>
        )}
      </div>


      <div className="expense-filters">
        <input
          type="text"
          placeholder="Search description or supplier"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="">All Categories</option>
          <option value="Food">Food</option>
          <option value="Fuel">Fuel</option>
          <option value="Supplies">Supplies</option>
          <option value="Maintenance">Maintenance</option>
          <option value="Utilities">Utilities</option>
          <option value="Misc">Misc</option>
        </select>
        <input
          type="date"
          value={dateFilter.from}
          onChange={(e) => setDateFilter((prev) => ({ ...prev, from: e.target.value }))}
        />
        <input
          type="date"
          value={dateFilter.to}
          onChange={(e) => setDateFilter((prev) => ({ ...prev, to: e.target.value }))}
        />
        <select
          value={vendorFilter}
          onChange={(e) => setVendorFilter(e.target.value)}
        >
          <option value="">All Vendors</option>
          {vendors.map(v => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="unsettled">Unsettled</option>
          <option value="settled">Settled</option>
        </select>
        <select value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)}>
          <option value="">All Suppliers</option>
          {uniqueSuppliers.map((s, idx) => (
            <option key={idx} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div className="premium-table-wrapper">
        <table className="premium-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Amount</th>
              <th>Category</th>
              <th>Supplier</th>
              <th>Vendor</th>
              <th>Date</th>
              <th>Status</th>
              {!hideActions && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {currentExpenses.map((exp) => (
              <tr key={exp.id}>
                <td>{exp.description}</td>
                <td className="font-bold">₦{Number(exp.amount).toLocaleString()}</td>
                <td><span className="premium-badge">{exp.category}</span></td>
                <td>{exp.supplier || "-"}</td>
                <td>{exp.vendor_name || "-"}</td>
                <td>{new Date(exp.expense_date).toLocaleDateString()}</td>
                <td>
                  <span className={`status-badge ${exp.status === 'settled' ? 'settled' : 'unsettled'}`}>
                    {exp.status || 'unsettled'}
                  </span>
                </td>
                <td>
                  {!hideActions && (
                    <div className="action-buttons">
                      {exp.status === 'unsettled' && (
                        <button className="item-action-btn view settle-btn" onClick={() => handleSettle(exp.id)}>
                          Settle
                        </button>
                      )}
                      <button className="item-action-btn view" onClick={() => handleEdit(exp)}>
                        Edit
                      </button>
                      <button className="item-action-btn delete" onClick={() => confirmDelete(exp)}>
                        Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="premium-card-list">
        {currentExpenses.map((exp) => (
          <div key={exp.id} className="premium-card">
            <div className="card-row header">
              <span className="card-title">{exp.description}</span>
              <span className="card-amount">₦{Number(exp.amount).toLocaleString()}</span>
            </div>

            <div className="card-row">
              <span className="card-label">Category</span>
              <span className="premium-badge">{exp.category}</span>
            </div>

            <div className="card-row">
              <span className="card-label">Supplier</span>
              <span>{exp.supplier || "-"}</span>
            </div>

            <div className="card-row">
              <span className="card-label">Date</span>
              <span>{new Date(exp.expense_date).toLocaleDateString()}</span>
            </div>

            <div className="card-row">
              <span className="card-label">Status</span>
              <span className={`status-badge ${exp.status === 'settled' ? 'settled' : 'unsettled'}`}>
                {exp.status || 'unsettled'}
              </span>
            </div>

            {!hideActions && (
              <div className="card-actions">
                {exp.status === 'unsettled' && (
                  <button className="primary-btn settle-btn" onClick={() => handleSettle(exp.id)}>Settle</button>
                )}
                <button className="secondary-btn" onClick={() => handleEdit(exp)}>Edit</button>
                <button className="danger-btn" onClick={() => confirmDelete(exp)}>Delete</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Prev
          </button>
          {renderPageNumbers()}
          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onConfirm={handleDeleteConfirmed}
        onCancel={handleDeleteCancelled}
        message={`Are you sure you want to delete "${expenseToDelete?.description}"?`}
      />
    </div>
  );
};

export default ExpenseList;
